<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>行銷管理</h1>
    <button class="btn-primary-add" @click="openAddCouponModal">
      + 新增優惠券
    </button>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋優惠券名稱..."
        @input="handleSearch"
      />
    </div>

    <div class="filter-dropdown">
      <label class="checkbox-label">
        <input type="checkbox" v-model="showExpiredCoupons" />
        <span>顯示已過期優惠券</span>
      </label>
    </div>
  </div>

  <!-- 手機版 - 優惠券卡片列表 -->
  <div class="coupon-list-mobile">
    <div
      class="coupon-card"
      v-for="coupon in pagedCoupons"
      :key="coupon.id"
      :class="{ expired: coupon.isExpired }"
    >
      <!-- 優惠券標題列 -->
      <div class="coupon-header">
        <div class="coupon-title-section">
          <div class="coupon-basic-info">
            <h3 class="coupon-name">{{ coupon.couponName }}</h3>
            <div class="badges-group">
              <span
                class="coupon-type-badge"
                :class="getCouponTypeClass(coupon.couponType)"
              >
                {{ getCouponTypeLabel(coupon.couponType) }}
              </span>
              <span class="status-badge" :class="getStatusClass(coupon)">
                {{ getStatusText(coupon) }}
              </span>
            </div>
          </div>
        </div>
        <div class="coupon-actions-top coupon-actions-desktop">
          <button
            class="btn-icon-small"
            @click="editCoupon(coupon)"
            title="編輯"
          >
            <img src="@/assets/image/icon/edit.png" class="all-icon" />
          </button>
          <button
            class="btn-icon-small delete"
            @click="deleteCoupon(coupon.id)"
            title="刪除"
          >
            <img src="@/assets/image/icon/trash.png" class="all-icon" />
          </button>
        </div>

        <!-- 手機/平板版文字按鈕 -->
        <div class="coupon-actions-mobile">
          <button class="btn-mobile-coupon-action" @click="editCoupon(coupon)">
            編輯
          </button>

          <button
            class="btn-mobile-coupon-action delete"
            @click="deleteCoupon(coupon.id)"
          >
            刪除
          </button>
        </div>
      </div>

      <!-- 優惠券資訊 -->
      <div class="coupon-info">
        <div class="info-row">
          <span class="info-label">優惠內容</span>
          <span class="info-value">{{ getCouponAmountText(coupon) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">有效期間</span>
          <span class="info-value"
            >{{ coupon.startDate }} ~ {{ coupon.endDate }}</span
          >
        </div>
        <div class="info-row" v-if="coupon.maxUsageCount > 0">
          <span class="info-label">發放數量</span>
          <span class="info-value">{{ coupon.maxUsageCount }} 次</span>
        </div>
        <div class="info-row" v-if="coupon.description">
          <span class="info-label">說明</span>
          <span class="info-value">{{ coupon.description }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- 手機版分頁 -->
  <div class="pagination-container mobile-pagination">
    <button
      class="pagination-btn"
      :disabled="currentPage === 1"
      @click="prevPage"
    >
      ⬅ 上一頁
    </button>

    <span class="pagination-info">
      第 {{ currentPage }} / {{ totalPages }} 頁
    </span>

    <button
      class="pagination-btn"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      下一頁 ➡
    </button>
  </div>

  <!-- 電腦版 - 優惠券表格 -->
  <div class="coupon-table-desktop">
    <div class="table-container">
      <table class="coupon-table">
        <thead>
          <tr>
            <th>優惠券名稱</th>
            <th style="text-align: center">優惠券類型</th>
            <th style="text-align: center">優惠內容</th>
            <th style="text-align: center">有效期間</th>
            <th style="text-align: center">已使用數量</th>
            <th style="text-align: center">發放數量</th>
            <th style="text-align: center">狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="coupon in pagedCoupons"
            :key="coupon.id"
            class="table-row"
            :class="{ expired: coupon.isExpired }"
          >
            <td>
              <div class="coupon-cell">
                <div class="coupon-name-text">
                  <div class="name-main">{{ coupon.couponName }}</div>
                  <div class="name-sub" v-if="coupon.description">
                    {{ coupon.description }}
                  </div>
                </div>
              </div>
            </td>
            <td class="type-cell">
              <span
                class="coupon-type-badge"
                :class="getCouponTypeClass(coupon.couponType)"
              >
                {{ getCouponTypeLabel(coupon.couponType) }}
              </span>
            </td>
            <td class="amount-cell">
              <span class="amount-text">{{ getCouponAmountText(coupon) }}</span>
            </td>
            <td class="date-cell">
              <div class="date-range">
                <div>{{ coupon.startDate }}</div>
                <div class="date-separator">~</div>
                <div>{{ coupon.endDate }}</div>
              </div>
            </td>
            <td class="limit-cell">
              <span class="limit-text">
                {{
                  coupon.usedCount > 0 ? `${coupon.usedCount} 次` : "尚未使用"
                }}
              </span>
            </td>
            <td class="limit-cell">
              <span class="limit-text">
                {{
                  coupon.maxUsageCount > 0
                    ? `${coupon.maxUsageCount} 次`
                    : "無限制"
                }}
              </span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="getStatusClass(coupon)">
                {{ getStatusText(coupon) }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action"
                  @click="editCoupon(coupon)"
                  title="編輯"
                >
                  <img src="@/assets/image/icon/edit.png" class="all-icon" />
                </button>
                <button
                  class="btn-table-action delete"
                  @click="deleteCoupon(coupon.id)"
                  title="刪除"
                >
                  <img src="@/assets/image/icon/trash.png" class="all-icon" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 空狀態 -->
      <div class="empty-state" v-if="filteredCoupons.length === 0">
        <div class="empty-icon">🎫</div>
        <p class="empty-text">目前無符合條件的優惠券</p>
      </div>

      <!-- 電腦版分頁 -->
      <div class="pagination-container">
        <button
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="prevPage"
        >
          ⬅ 上一頁
        </button>

        <span class="pagination-info">
          第 {{ currentPage }} / {{ totalPages }} 頁
        </span>

        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          下一頁 ➡
        </button>
      </div>
    </div>
  </div>

  <!-- 新增/編輯優惠券 Modal -->
  <div class="modal" v-if="showCouponModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ editingCoupon ? "編輯優惠券" : "新增優惠券" }}</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <form @submit.prevent="saveCoupon" class="modal-body">
        <div class="form-row">
          <div class="form-group" style="flex: 1">
            <label class="form-label required">優惠券名稱</label>
            <input
              type="text"
              class="form-input"
              v-model="couponForm.couponName"
              placeholder="請輸入優惠券名稱"
              required
            />
          </div>

          <!-- 啟用狀態 -->
          <div class="form-group">
            <label class="form-label required">優惠狀態</label>
            <select class="form-input" v-model="couponForm.isActive" required>
              <option :value="true">啟用</option>
              <option :value="false">禁用</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label required">優惠類型</label>
            <select class="form-input" v-model="couponForm.couponType" required>
              <option value="">請選擇優惠類型</option>
              <option value="Fixed">固定金額折抵</option>
              <option value="Percentage">百分比折扣</option>
              <option value="Product">兌換商品</option>
            </select>
          </div>

          <!-- 固定金額 / 百分比才出現 -->
          <div class="form-group" v-if="couponForm.couponType !== 'Product'">
            <label class="form-label required">
              {{
                couponForm.couponType === "Fixed" ? "折扣金額" : "折扣百分比"
              }}
            </label>

            <div style="display: flex; align-items: center">
              <input
                type="number"
                class="form-input"
                v-model="couponForm.discountValue"
                :placeholder="
                  couponForm.couponType === 'Fixed' ? '例如:100' : '例如:20'
                "
                :min="couponForm.couponType === 'Percentage' ? 1 : 0"
                :max="couponForm.couponType === 'Percentage' ? 100 : undefined"
                required
                style="flex: 1"
              />

              <!-- 百分比時加上 % -->
              <span
                v-if="couponForm.couponType === 'Percentage'"
                style="margin-left: 6px; font-weight: bold"
              >
                %
              </span>
            </div>
          </div>
          <!-- 只有兌換商品時顯示 -->
          <div class="form-group" v-if="couponForm.couponType === 'Product'">
            <label class="form-label required">選擇兌換商品</label>
            <select class="form-input" v-model="couponForm.itemId" required>
              <option disabled value="0">請選擇商品</option>
              <option
                v-for="item in productList"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label required">開始日期</label>
            <input
              type="date"
              class="form-input"
              v-model="couponForm.startDate"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label required">結束日期</label>
            <input
              type="date"
              class="form-input"
              v-model="couponForm.endDate"
              :min="couponForm.startDate"
              required
            />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">最大發放數量</label>
          <input
            type="number"
            class="form-input"
            v-model="couponForm.maxUsageCount"
            placeholder="設為 0 表示無限制"
            min="0"
          />
        </div>

        <div class="form-group">
          <label class="form-label">優惠券描述</label>
          <textarea
            class="form-input"
            v-model="couponForm.description"
            placeholder="請輸入優惠券詳細說明"
            rows="3"
          ></textarea>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="closeModal">
            取消
          </button>
          <button type="submit" class="btn-submit">
            {{ editingCoupon ? "儲存變更" : "新增優惠券" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script src="@/assets/js/market.js"></script>
<style scoped src="@/assets/css/market.css"></style>
